import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload,
  RocketLaunch,
  CheckCircle,
  Error as ErrorIcon,
  Info,
} from '@mui/icons-material';
import { mt5API } from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';

const LOG_LEVEL_ICON = {
  info:    <Info fontSize="small" />,
  log:     null,
  success: <CheckCircle fontSize="small" />,
  error:   <ErrorIcon fontSize="small" />,
};

export default function DeployTab() {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deployKey, setDeployKey] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);
  const fileInputRef = useRef(null);
  const logsEndRef = useRef(null);

  const { lastMessage } = useWebSocket();
  const resetTimerRef = useRef(null);

  const resetState = useCallback(() => {
    setFile(null);
    setLogs([]);
    setError('');
    setDeploying(false);
  }, []);

  // Append deploy result messages streamed from console-app via WebSocket
  useEffect(() => {
    if (!lastMessage || lastMessage.type !== 'deploy_ea_result') return;
    const { level, message, timestamp } = lastMessage;
    setLogs(prev => [...prev, { level, message, timestamp }]);
  }, [lastMessage]);

  // Clean up timer on unmount
  useEffect(() => () => { if (resetTimerRef.current) clearTimeout(resetTimerRef.current); }, []);

  // Auto-scroll log output to bottom
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const acceptFile = useCallback((f) => {
    if (!f) return;
    if (!/\.mq[45]$/i.test(f.name)) {
      setError('Chỉ chấp nhận file .mq4 hoặc .mq5');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('File quá lớn (tối đa 10 MB)');
      return;
    }
    setFile(f);
    setError('');
    setLogs([]);
  }, []);

  const handleFileChange = (e) => acceptFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    acceptFile(e.dataTransfer.files[0]);
  };

  const handleDeploy = async () => {
    if (!deployKey) {
      setError('Vui lòng nhập mật khẩu triển khai');
      return;
    }
    setConfirmOpen(false);
    setDeploying(true);
    setError('');
    setLogs([]);

    try {
      const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      await mt5API.deployEA({ filename: file.name, content, deployKey });
    } catch (err) {
      const msg = err.response?.data?.error || 'Gửi lệnh deploy thất bại';
      setError(msg);
    } finally {
      // Always reset to original state after 3 s regardless of WS result
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(resetState, 3000);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Deploy EA</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload file .mq5 / .mq4 và triển khai trực tiếp tới MetaEditor thông qua console-app.
      </Typography>

      {/* Drop zone */}
      <Paper
        variant="outlined"
        onClick={() => !file && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={{
          p: 4,
          mb: 3,
          textAlign: 'center',
          cursor: file ? 'default' : 'pointer',
          border: dragging ? '2px dashed #1976d2' : '2px dashed',
          borderColor: dragging ? 'primary.main' : 'divider',
          bgcolor: dragging ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".mq4,.mq5"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />

        {file ? (
          <Box>
            <Chip
              label={file.name}
              color="primary"
              variant="outlined"
              onDelete={() => { setFile(null); setLogs([]); }}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              Kéo thả file .mq5 / .mq4 vào đây
            </Typography>
            <Typography variant="caption" color="text.secondary">
              hoặc click để chọn file (tối đa 10 MB)
            </Typography>
          </>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Button
        variant="contained"
        size="large"
        startIcon={deploying ? <CircularProgress size={18} color="inherit" /> : <RocketLaunch />}
        disabled={!file || deploying}
        onClick={() => { setDeployKey(''); setConfirmOpen(true); }}
        sx={{ mb: 3 }}
      >
        {deploying ? 'Đang triển khai...' : 'Deploy'}
      </Button>

      {deploying && <LinearProgress sx={{ mb: 2 }} />}

      {/* Deploy log output */}
      {logs.length > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Kết quả triển khai</Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              maxHeight: 320,
              overflowY: 'auto',
              bgcolor: '#0a0a0a',
              fontFamily: 'monospace',
            }}
          >
            {logs.map((entry, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  mb: 0.5,
                  color:
                    entry.level === 'success' ? '#4caf50' :
                    entry.level === 'error'   ? '#f44336' :
                    entry.level === 'info'    ? '#2196f3' :
                    '#e0e0e0',
                }}
              >
                {LOG_LEVEL_ICON[entry.level] && (
                  <Box sx={{ flexShrink: 0, mt: '2px', fontSize: 14 }}>
                    {LOG_LEVEL_ICON[entry.level]}
                  </Box>
                )}
                <Typography
                  component="pre"
                  sx={{
                    m: 0,
                    fontSize: '0.78rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    color: 'inherit',
                  }}
                >
                  {entry.message}
                </Typography>
              </Box>
            ))}
            <div ref={logsEndRef} />
          </Paper>
        </>
      )}

      {/* Confirmation dialog with deploy key input */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Xác nhận triển khai</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Bạn sắp deploy <strong>{file?.name}</strong> tới MetaEditor.
            Nhập mật khẩu triển khai để xác nhận.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Mật khẩu triển khai"
            value={deployKey}
            onChange={(e) => setDeployKey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && deployKey && handleDeploy()}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!deployKey}
            onClick={handleDeploy}
            startIcon={<RocketLaunch />}
          >
            Xác nhận Deploy
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
